
import { useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Settings, LogOut, CreditCard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { signOutUser } from "@/lib/firebase";
import { toast } from "sonner";
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export function UserMenu() {
  const { user } = useAuth();
  const [showSettings, setShowSettings] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOutUser();
      toast.success("Signed out successfully");
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Failed to sign out");
    }
  };

  const loadPayments = async () => {
    if (!user) return;
    setIsLoadingPayments(true);
    try {
      const paymentsQuery = query(collection(db, 'funnels'), where('userId', '==', user.uid), where('isPro', '==', true));
      const querySnapshot = await getDocs(paymentsQuery);
      const upgradeHistory = querySnapshot.docs.map(doc => ({
        id: doc.id,
        siteName: doc.data().siteName,
        upgradedAt: doc.data().upgradedAt
      }));
      setPayments(upgradeHistory);
    } catch (error) {
      console.error('Error loading payments:', error);
      toast.error('Failed to load payment history');
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
    loadPayments();
  };

  const initials = user?.email?.split('@')[0].slice(0, 2).toUpperCase() || '??';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-8 w-8 rounded-full">
            <Avatar className="h-8 w-8">
              {user?.photoURL && <AvatarImage src={user.photoURL} alt={user.email || 'User avatar'} />}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {user?.displayName || "Account"}
              </p>
              <p className="text-xs leading-none text-muted-foreground">
                {user?.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleOpenSettings}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Beállítások</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>Kilépés</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {showSettings && (
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Profil beállítások</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-4">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Funnel Upgrades History
                </h4>
                {isLoadingPayments ? (
                  <p className="text-sm text-muted-foreground">Loading...</p>
                ) : payments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No upgrades yet</p>
                ) : (
                  <div className="space-y-2">
                    {payments.map(payment => (
                      <div key={payment.id} className="text-sm border rounded-lg p-3 space-y-1">
                        <p className="font-medium">{payment.siteName}</p>
                        <p className="text-xs text-muted-foreground">
                          Upgraded on {new Date(payment.upgradedAt).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
